import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `betting` domain — the five tabs of `/betting`.
 *
 * The load-bearing entry is `winloss`, which appears on two tabs and is stored
 * PLAYER-side (`payout − stake`, apps/bet/src/repo/bets.ts:368). The table
 * renders it as a magnitude plus an arrow, so green-up means the player won and
 * the house lost — the exact opposite of the home dashboard, which negates the
 * same number before display (pages/DashboardPage.tsx:290-295). Both `pos` and
 * `neg` therefore name the winner and the loser explicitly.
 */
export const bettingGuide: DomainGuide = {
  domain: 'betting',
  pages: [
    {
      id: 'betting.bets',
      route: '/betting → Bets',
      permission: 'bet:read',
      status: 'live',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'round', kind: 'text' },
        { key: 'stake', kind: 'money-unsigned' },
        { key: 'payout', kind: 'money-unsigned' },
        { key: 'winloss', kind: 'money-signed', formula: 'winloss = payout − stake' },
        { key: 'turnover', kind: 'money-unsigned' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 7,
      warningCount: 4,
    },
    {
      id: 'betting.outstanding',
      // bet.bet_outstandings has TWO insert sites, and #188 added the one that
      // matters: openExposure (seamless-projection.ts:55) opens a row for every
      // seamless provider round, which is every real bet. placeBet
      // (repo/bets.ts:238) is the other, reached only by the internal
      // POST /seamless/bet — POST /bets/place is gone. So this tab is live, not
      // partially-wired, and its hazard inverted: rows now appear, and a
      // provider that closes a losing round by sending nothing (PG Soft) leaves
      // one standing forever. Age here means "not closed yet", not "stuck".
      // The lifecycle is not one-way either: projectUnsettled taking back the
      // last standing credit returns the row to 'placed' and RE-opens exposure,
      // so a settled bet can reappear on this tab. The backoffice's own
      // cancelPayout does not — it stamps 'payout_cancelled' and leaves the
      // index alone — which is why the copy separates the two.
      route: '/betting → Outstanding',
      permission: 'bet:read',
      status: 'live',
      fields: [
        { key: 'placedAt', kind: 'date' },
        { key: 'age', kind: 'text' },
        { key: 'betId', kind: 'text' },
        { key: 'stake', kind: 'money-unsigned' },
      ],
      cautionCount: 6,
      warningCount: 1,
    },
    {
      id: 'betting.winloss',
      route: '/betting → Win/Loss',
      permission: 'bet:read',
      status: 'live',
      fields: [
        { key: 'stampHour', kind: 'date' },
        { key: 'betCount', kind: 'count' },
        { key: 'turnover', kind: 'money-unsigned' },
        { key: 'payout', kind: 'money-unsigned' },
        { key: 'winloss', kind: 'money-signed', formula: 'winloss = payout − stake' },
      ],
      cautionCount: 6,
      warningCount: 0,
    },
    {
      id: 'betting.limits',
      // Gateway gates the WHOLE bet-limits resource — reads included — on
      // bet:update (api-gateway/src/upstreams.ts:237): the policy carries no
      // `methods`, so a bet:read-only operator gets 403 opening the tab.
      // Enforcement happens in svc-game's pre-debit gate (BET_LIMIT_GATE →
      // POST /bet-limits/internal/check), which fails OPEN and receives no
      // group id, so group-scope rules never match — hence partially-wired.
      // svc-bet's POST /bets/place, which is what the rules used to be checked
      // in, no longer exists.
      route: '/betting → Limits',
      permission: 'bet:update',
      status: 'partially-wired',
      fields: [
        { key: 'scope', kind: 'text' },
        { key: 'scopeId', kind: 'text' },
        { key: 'gameType', kind: 'text' },
        { key: 'maxPerBet', kind: 'money-unsigned' },
        { key: 'maxPerDay', kind: 'money-unsigned' },
        { key: 'maxPerMonth', kind: 'money-unsigned' },
        { key: 'isActive', kind: 'status' },
      ],
      cautionCount: 8,
      warningCount: 1,
    },
    {
      id: 'betting.disputes',
      // The queue for the post-hoc maker-checker re-check in svc-bet
      // (repo/bet-admin-actions.ts, detectThresholdBreach). That gate is
      // PRE-FLIGHT by necessity, so it decides against a `payout` read before
      // the row is locked; a settle top-up landing inside the window makes the
      // real claw-back larger than the figure the gate weighed. It cannot
      // un-move money, so it marks the row `flagged` instead of `resolved`.
      // Both figures now live on the row (migration
      // apps/bet/db/migrations/0012_bet_disputes_review.sql) — before it they
      // survived only in a WARN line and in
      // env.BET_ADMIN_APPROVAL_THRESHOLD_MINOR, which can be re-tuned.
      //
      // Reading is gated on bet:read and closing on bet:approve
      // (api-gateway/src/upstreams.ts, resource `bet-disputes`, split by
      // method) — deliberately NOT the `bets` resource, whose every write is
      // gated on bet:settle, the broadest money permission.
      route: '/betting → Flagged disputes',
      permission: 'bet:read',
      status: 'live',
      fields: [
        { key: 'moneyDelta', kind: 'money-signed' },
        { key: 'evaluatedMoneyDelta', kind: 'money-signed' },
        { key: 'thresholdMinor', kind: 'money-unsigned' },
        { key: 'status', kind: 'status' },
        { key: 'review', kind: 'text' },
      ],
      cautionCount: 6,
      warningCount: 1,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
